import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Container,
  Typography,
  Paper,
  Box,
  Grid,
  Chip,
  Avatar,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  LinearProgress,
  Tab,
  Tabs,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  PersonAdd as PersonAddIcon,
  ArrowBack as ArrowBackIcon,
  Group as GroupIcon,
  ViewKanban as ViewKanbanIcon,
  CalendarMonth as CalendarMonthIcon,
  BarChart as BarChartIcon,
  Search as SearchIcon,
  ChevronLeft as PrevIcon,
  ChevronRight as NextIcon,
} from '@mui/icons-material';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import {
  format, startOfMonth, endOfMonth, startOfWeek, addDays, isSameDay,
  isSameMonth, addMonths, subMonths, parseISO,
} from 'date-fns';
import { Gantt as GanttChart, Task as GanttTaskType, ViewMode } from 'gantt-task-react';
import 'gantt-task-react/dist/index.css';
import { getGanttData, GanttTask as GanttTaskData } from '../services/reportService';
import { AppDispatch, RootState } from '../store';
import { fetchProject, fetchProjectMembers, deleteProject, updateProject } from '../store/slices/projectSlice';
import projectService, { ProjectTeam } from '../services/projectService';
import teamService, { Team } from '../services/teamService';
import { taskService, CreateTaskData, priorityToNumber } from '../services/taskService';
import { taskTypeService, TaskType } from '../services/taskTypeService';
import { taskStatusService, TaskStatus } from '../services/taskStatusService';
import { getUsers, User } from '../services/userService';
import { roleService, Role } from '../services/roleService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

// --- Board Tab Components ---
const ITEM_TYPE = 'TASK_CARD';

const getPriorityLabel = (p: string | number) => {
  const map: Record<string, string> = { '1': 'Low', '2': 'Medium', '3': 'High', '4': 'Urgent' };
  return map[String(p)] || String(p);
};

const getPriorityColor = (p: string | number) => {
  switch (String(p)) {
    case '4': return '#EF4444';
    case '3': return '#F59E0B';
    case '2': return '#3B82F6';
    case '1': return '#10B981';
    default: return '#6B7280';
  }
};

const priorityColors: Record<string, string> = {
  '1': '#10B981', '2': '#3B82F6', '3': '#F59E0B', '4': '#EF4444',
};

interface BoardTaskCard {
  id: string;
  title: string;
  statusId: string;
  priority: number | string;
  assignee?: { id: string; firstName: string; lastName: string };
  dueDate?: string;
  labels: { id: string; name: string; color?: string }[];
  taskTypeName?: string;
}

const DraggableTaskCard: React.FC<{ task: BoardTaskCard; onClick: () => void }> = ({ task, onClick }) => {
  const [{ isDragging }, drag] = useDrag({
    type: ITEM_TYPE,
    item: { id: task.id, statusId: task.statusId },
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  return (
    <Paper
      ref={drag}
      elevation={isDragging ? 4 : 1}
      onClick={onClick}
      sx={{
        p: 1.5, mb: 1, cursor: 'grab', opacity: isDragging ? 0.5 : 1,
        borderLeft: `3px solid ${getPriorityColor(task.priority)}`,
        '&:hover': { bgcolor: 'action.hover' }, transition: 'box-shadow 0.2s',
      }}
    >
      <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>{task.title}</Typography>
      {task.labels && task.labels.length > 0 && (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 0.5 }}>
          {task.labels.map((l) => (
            <Chip key={l.id} label={l.name} size="small"
              sx={{ height: 18, fontSize: '0.65rem', bgcolor: l.color || '#e0e0e0', color: '#fff' }} />
          ))}
        </Box>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
        <Chip label={getPriorityLabel(task.priority)} size="small"
          sx={{ height: 20, fontSize: '0.65rem', bgcolor: getPriorityColor(task.priority), color: '#fff' }} />
        {task.assignee ? (
          <Tooltip title={`${task.assignee.firstName} ${task.assignee.lastName}`}>
            <Avatar sx={{ width: 24, height: 24, fontSize: '0.7rem' }}>{task.assignee.firstName[0]}</Avatar>
          </Tooltip>
        ) : (
          <Avatar sx={{ width: 24, height: 24, fontSize: '0.7rem', bgcolor: '#e0e0e0' }}>?</Avatar>
        )}
      </Box>
      {task.dueDate && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          Due: {new Date(task.dueDate).toLocaleDateString()}
        </Typography>
      )}
    </Paper>
  );
};

const KanbanColumn: React.FC<{
  status: TaskStatus;
  tasks: BoardTaskCard[];
  onDrop: (taskId: string, newStatusId: string) => void;
  onTaskClick: (taskId: string) => void;
}> = ({ status, tasks, onDrop, onTaskClick }) => {
  const [{ isOver }, drop] = useDrop({
    accept: ITEM_TYPE,
    drop: (item: { id: string; statusId: string }) => {
      if (item.statusId !== status.id) onDrop(item.id, status.id);
    },
    collect: (monitor) => ({ isOver: monitor.isOver() }),
  });

  return (
    <Box ref={drop} sx={{
      minWidth: 260, maxWidth: 300, flex: '0 0 280px', display: 'flex', flexDirection: 'column',
      bgcolor: isOver ? 'action.hover' : 'grey.50', borderRadius: 2, overflow: 'hidden',
      height: 'fit-content', maxHeight: 500,
    }}>
      <Box sx={{
        p: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        borderBottom: `3px solid ${status.color || '#6B7280'}`, bgcolor: 'background.paper',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle2" fontWeight={700}>{status.name}</Typography>
          <Chip label={tasks.length} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
        </Box>
      </Box>
      <Box sx={{ p: 1, overflowY: 'auto', flexGrow: 1 }}>
        {tasks.map((task) => (
          <DraggableTaskCard key={task.id} task={task} onClick={() => onTaskClick(task.id)} />
        ))}
        {tasks.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>No tasks</Typography>
        )}
      </Box>
    </Box>
  );
};

const ProjectDetailsPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  
  const { selectedProject: project, projectMembers, loading } = useSelector((state: RootState) => state.projects);

  const [tabValue, setTabValue] = useState(0);
  const [tasks, setTasks] = useState<any[]>([]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [createTaskDialogOpen, setCreateTaskDialogOpen] = useState(false);
  const [addMemberDialogOpen, setAddMemberDialogOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({
    name: '',
    description: '',
    color: '',
    startDate: '',
    endDate: '',
    status: '',
  });
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [newTaskDescription, setNewTaskDescription] = useState('');
  const [newTaskPriority, setNewTaskPriority] = useState<'low' | 'medium' | 'high' | 'urgent'>('medium');
  const [newTaskTypeId, setNewTaskTypeId] = useState('');
  const [newTaskAssigneeId, setNewTaskAssigneeId] = useState('');
  const [newTaskStartDate, setNewTaskStartDate] = useState('');
  const [newTaskDueDate, setNewTaskDueDate] = useState('');
  const [taskTypes, setTaskTypes] = useState<TaskType[]>([]);
  const [taskStatuses, setTaskStatuses] = useState<TaskStatus[]>([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedMemberRole, setSelectedMemberRole] = useState('');
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [availableRoles, setAvailableRoles] = useState<Role[]>([]);
  const [projectTeams, setProjectTeams] = useState<ProjectTeam[]>([]);
  const [availableTeams, setAvailableTeams] = useState<Team[]>([]);
  const [assignTeamDialogOpen, setAssignTeamDialogOpen] = useState(false);
  const [selectedTeamId, setSelectedTeamId] = useState<string>('');

  // Board tab state
  const [boardSearchTerm, setBoardSearchTerm] = useState('');

  // Calendar tab state
  const [calendarDate, setCalendarDate] = useState(new Date());

  // Gantt tab state
  const [ganttViewMode, setGanttViewMode] = useState<ViewMode>(ViewMode.Week);
  const [ganttApiData, setGanttApiData] = useState<GanttTaskData[]>([]);

  const loadProjectTasks = async () => {
    if (!projectId) return;
    try {
      const projectTasks = await taskService.getTasks({ projectId });
      setTasks(projectTasks as any[]);
    } catch (error) {
      console.error('Failed to load project tasks', error);
    }
  };

  useEffect(() => {
    if (projectId) {
      dispatch(fetchProject(projectId));
      dispatch(fetchProjectMembers(projectId));
      loadProjectTasks();
      loadProjectTeams();
      loadAvailableTeams();
      loadAvailableRoles();
      loadAvailableUsers();
      loadTaskTypes();
      loadTaskStatuses();
    }
  }, [dispatch, projectId]);

  const loadProjectTeams = async () => {
    if (projectId) {
      try {
        const teams = await projectService.getProjectTeams(projectId);
        setProjectTeams(teams);
      } catch (error) {
        console.error('Failed to load project teams', error);
      }
    }
  };

  const loadAvailableTeams = async () => {
    try {
      const teams = await teamService.getAllTeams();
      setAvailableTeams(teams);
    } catch (error) {
      console.error('Failed to load available teams', error);
    }
  };

  const loadAvailableUsers = async () => {
    try {
      const users = await getUsers();
      setAvailableUsers(users);
    } catch (error) {
      console.error('Failed to load available users', error);
    }
  };

  const loadAvailableRoles = async () => {
    try {
      const roles = await roleService.getRoles();
      setAvailableRoles(roles);
      // Set default role to "Member" if available
      const memberRole = roles.find(r => r.name.toLowerCase() === 'member');
      if (memberRole) {
        setSelectedMemberRole(memberRole.id);
      } else if (roles.length > 0) {
        setSelectedMemberRole(roles[0].id);
      }
    } catch (error) {
      console.error('Failed to load available roles', error);
    }
  };

  const loadTaskTypes = async () => {
    try {
      const types = await taskTypeService.getTaskTypes();
      setTaskTypes(types);
      // Set default task type if available
      const defaultType = types.find(t => t.isDefault);
      if (defaultType) {
        setNewTaskTypeId(defaultType.id);
      } else if (types.length > 0) {
        setNewTaskTypeId(types[0].id);
      }
    } catch (error) {
      console.error('Failed to load task types', error);
    }
  };

  const loadTaskStatuses = async () => {
    try {
      const statuses = await taskStatusService.getTaskStatuses();
      setTaskStatuses(statuses);
    } catch (error) {
      console.error('Failed to load task statuses', error);
    }
  };

  useEffect(() => {
    if (project) {
      setEditFormData({
        name: project.name,
        description: project.description || '',
        color: project.color || '#3B82F6',
        startDate: project.startDate ? new Date(project.startDate).toISOString().split('T')[0] : '',
        endDate: project.endDate ? new Date(project.endDate).toISOString().split('T')[0] : '',
        status: project.status || 'active',
      });
    }
  }, [project]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    // Load gantt data when switching to Gantt tab
    if (newValue === 6 && projectId && ganttApiData.length === 0) {
      getGanttData(projectId).then(d => setGanttApiData(d)).catch(() => {});
    }
  };

  const handleEditOpen = () => {
    setEditDialogOpen(true);
  };

  const handleEditSave = async () => {
    if (projectId) {
      try {
        await dispatch(updateProject({
          id: projectId,
          data: {
            name: editFormData.name,
            description: editFormData.description,
            color: editFormData.color,
            startDate: editFormData.startDate || undefined,
            endDate: editFormData.endDate || undefined,
            status: editFormData.status,
          },
        })).unwrap();
        setEditDialogOpen(false);
      } catch (error: any) {
        console.error('Failed to update project:', error);
        alert('Failed to update project: ' + (error.message || 'Unknown error'));
      }
    }
  };

  const handleDelete = async () => {
    if (projectId) {
      await dispatch(deleteProject(projectId));
      navigate('/projects');
    }
  };

  const handleAssignTeam = async () => {
    if (projectId && selectedTeamId) {
      try {
        await projectService.assignTeamToProject(projectId, selectedTeamId);
        setAssignTeamDialogOpen(false);
        setSelectedTeamId('');
        loadProjectTeams();
      } catch (error) {
        console.error('Failed to assign team', error);
      }
    }
  };

  const handleUnassignTeam = async (teamId: string) => {
    if (projectId && window.confirm('Are you sure you want to remove this team from the project?')) {
      try {
        await projectService.unassignTeamFromProject(projectId, teamId);
        loadProjectTeams();
      } catch (error) {
        console.error('Failed to unassign team', error);
      }
    }
  };

  const handleCreateTask = async () => {
    if (!projectId || !newTaskTitle.trim() || !newTaskTypeId) return;
    
    try {
      const taskData: CreateTaskData = {
        projectId,
        title: newTaskTitle,
        description: newTaskDescription,
        priority: priorityToNumber(newTaskPriority),
        taskTypeId: newTaskTypeId,
        assigneeId: newTaskAssigneeId || undefined,
        startDate: newTaskStartDate || undefined,
        dueDate: newTaskDueDate || undefined,
      };

      await taskService.createTask(taskData);
      setCreateTaskDialogOpen(false);
      setNewTaskTitle('');
      setNewTaskDescription('');
      setNewTaskPriority('medium');
      setNewTaskAssigneeId('');
      setNewTaskStartDate('');
      setNewTaskDueDate('');
      // Reset to default task type
      const defaultType = taskTypes.find(t => t.isDefault);
      if (defaultType) {
        setNewTaskTypeId(defaultType.id);
      }
      loadProjectTasks();
      dispatch(fetchProject(projectId));
    } catch (error: any) {
      console.error('Failed to create task', error);
      console.error('Error response data:', error.response?.data);
      console.error('Sent task data:', {
        projectId,
        title: newTaskTitle,
        taskTypeId: newTaskTypeId,
      });
      
      // Display detailed validation errors if available
      let errorMessage = 'Unknown error';
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.errors) {
          // ValidationProblemDetails format
          errorMessage = Object.entries(error.response.data.errors)
            .map(([field, errors]: [string, any]) => `${field}: ${Array.isArray(errors) ? errors.join(', ') : errors}`)
            .join('\n');
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else {
          errorMessage = JSON.stringify(error.response.data);
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      alert('Failed to create task:\n' + errorMessage);
    }
  };

  const handleAddMember = async () => {
    if (!projectId || !selectedUserId || !selectedMemberRole) return;
    
    try {
      await projectService.addProjectMember(projectId, {
        userId: selectedUserId,
        roleId: selectedMemberRole,
      });
      setAddMemberDialogOpen(false);
      setSelectedUserId('');
      // Reset to default role
      const memberRole = availableRoles.find(r => r.name.toLowerCase() === 'member');
      if (memberRole) {
        setSelectedMemberRole(memberRole.id);
      }
      dispatch(fetchProjectMembers(projectId));
      dispatch(fetchProject(projectId));
    } catch (error: any) {
      console.error('Failed to add member', error);
      const errorMsg = error.response?.data || 'Please try again.';
      alert('Failed to add member. ' + errorMsg);
    }
  };

  // Gantt tab - transform API data to gantt-task-react format (must be before early return - hooks rule)
  const ganttTasks: GanttTaskType[] = useMemo(() => {
    const defaultStart = new Date();
    const parents = ganttApiData.filter(t => !t.parentTaskId);
    const childMap = new Map<string, GanttTaskData[]>();
    ganttApiData.filter(t => t.parentTaskId).forEach(t => {
      if (!childMap.has(t.parentTaskId!)) childMap.set(t.parentTaskId!, []);
      childMap.get(t.parentTaskId!)!.push(t);
    });
    const result: GanttTaskType[] = [];
    let order = 0;
    const scm: Record<string, { bg: string; pg: string }> = {
      todo: { bg: '#94A3B8', pg: '#64748B' },
      in_progress: { bg: '#3B82F6', pg: '#1D4ED8' },
      done: { bg: '#10B981', pg: '#059669' },
    };
    parents.forEach(task => {
      const start = task.startDate ? new Date(task.startDate) : defaultStart;
      const end = task.dueDate ? new Date(task.dueDate) : new Date(start.getTime() + 7 * 86400000);
      const colors = scm[task.statusCategory || 'todo'] || scm.todo;
      const children = childMap.get(task.id) || [];
      result.push({
        id: task.id, type: children.length > 0 ? 'project' : 'task', name: task.title,
        start, end: end > start ? end : new Date(start.getTime() + 86400000),
        progress: task.progress, hideChildren: false, displayOrder: order++,
        dependencies: task.dependencies?.filter(d => ganttApiData.some(t => t.id === d)) || [],
        styles: { backgroundColor: task.statusColor || colors.bg, progressColor: colors.pg },
      });
      children.forEach(sub => {
        const sStart = sub.startDate ? new Date(sub.startDate) : start;
        const sEnd = sub.dueDate ? new Date(sub.dueDate) : new Date(sStart.getTime() + 3 * 86400000);
        const sColors = scm[sub.statusCategory || 'todo'] || scm.todo;
        result.push({
          id: sub.id, type: 'task', name: sub.title,
          start: sStart, end: sEnd > sStart ? sEnd : new Date(sStart.getTime() + 86400000),
          progress: sub.progress, project: task.id, displayOrder: order++,
          dependencies: sub.dependencies?.filter(d => ganttApiData.some(t => t.id === d)) || [],
          styles: { backgroundColor: sub.statusColor || sColors.bg, progressColor: sColors.pg },
        });
      });
    });
    return result;
  }, [ganttApiData]);

  if (loading || !project) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <LinearProgress />
      </Container>
    );
  }

  const completedTasks = tasks.filter(t => t.taskStatus?.name === 'Done').length;
  const totalTasks = tasks.length;
  const progress = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;

  // Board tab - sorted statuses and filtered tasks
  const sortedStatuses = [...taskStatuses].sort((a, b) => a.position - b.position);
  const boardFilteredTasks = tasks.filter((t: any) =>
    !boardSearchTerm || t.title?.toLowerCase().includes(boardSearchTerm.toLowerCase())
  );

  const handleBoardDrop = async (taskId: string, newStatusId: string) => {
    // Optimistic update
    setTasks((prev: any[]) => prev.map(t => t.id === taskId ? { ...t, statusId: newStatusId } : t));
    try {
      await taskService.updateTask(taskId, { statusId: newStatusId });
    } catch (err) {
      console.error('Failed to update task status:', err);
      loadProjectTasks(); // Revert on error
    }
  };

  // Calendar tab - computed cells
  const calMonthStart = startOfMonth(calendarDate);
  const calStart = startOfWeek(calMonthStart, { weekStartsOn: 1 });
  const calendarCells = Array.from({ length: 42 }, (_, i) => addDays(calStart, i));

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/projects')}
          sx={{ mb: 2 }}
        >
          Back to Projects
        </Button>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              {project.name}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Chip label={project.key} size="small" />
              <Chip
                label={project.status}
                color={project.status === 'Active' ? 'success' : 'default'}
                size="small"
              />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton color="primary" onClick={handleEditOpen}>
              <EditIcon />
            </IconButton>
            <IconButton color="error" onClick={() => setDeleteDialogOpen(true)}>
              <DeleteIcon />
            </IconButton>
          </Box>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Total Tasks
            </Typography>
            <Typography variant="h4">{totalTasks}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Completed
            </Typography>
            <Typography variant="h4" color="success.main">
              {completedTasks}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Team Members
            </Typography>
            <Typography variant="h4">{projectMembers.length}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Progress
            </Typography>
            <Typography variant="h4">{progress.toFixed(0)}%</Typography>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{ mt: 1, height: 8, borderRadius: 1 }}
            />
          </Paper>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper>
        <Tabs value={tabValue} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
          <Tab label="Overview" />
          <Tab label="Tasks" />
          <Tab label="Members" />
          <Tab label="Teams" />
          <Tab icon={<ViewKanbanIcon />} iconPosition="start" label="Board" />
          <Tab icon={<CalendarMonthIcon />} iconPosition="start" label="Calendar" />
          <Tab icon={<BarChartIcon />} iconPosition="start" label="Gantt" />
        </Tabs>

        {/* Overview Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Description
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              {project.description || 'No description provided.'}
            </Typography>

            <Divider sx={{ my: 3 }} />

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Created
                </Typography>
                <Typography variant="body1">
                  {format(new Date(project.createdAt), 'MMM dd, yyyy')}
                </Typography>
              </Grid>
              {project.startDate && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Start Date
                  </Typography>
                  <Typography variant="body1">
                    {format(new Date(project.startDate), 'MMM dd, yyyy')}
                  </Typography>
                </Grid>
              )}
              {project.endDate && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    End Date
                  </Typography>
                  <Typography variant="body1">
                    {format(new Date(project.endDate), 'MMM dd, yyyy')}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Box>
        </TabPanel>

        {/* Tasks Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">Tasks</Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setCreateTaskDialogOpen(true)}
              >
                Create Task
              </Button>
            </Box>
            <List>
              {tasks.map((task) => (
                <React.Fragment key={task.id}>
                  <ListItem
                    button
                    onClick={() => navigate(`/tasks/${task.id}`)}
                  >
                    <ListItemText
                      primary={task.title}
                      secondary={
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
                          <Chip
                            label={task.taskStatus?.name || 'No Status'}
                            size="small"
                          />
                          {task.assignee && (
                            <Typography variant="caption" color="text.secondary">
                              Assigned to {task.assignee.firstName} {task.assignee.lastName}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))}
              {tasks.length === 0 && (
                <ListItem>
                  <ListItemText
                    primary="No tasks yet"
                    secondary="Create your first task to get started"
                  />
                </ListItem>
              )}
            </List>
          </Box>
        </TabPanel>

        {/* Team Tab */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">Team Members</Typography>
              <Button 
                variant="contained" 
                startIcon={<PersonAddIcon />}
                onClick={() => setAddMemberDialogOpen(true)}
              >
                Add Member
              </Button>
            </Box>
            <List>
              {projectMembers.map((member) => (
                <React.Fragment key={member.userId}>
                  <ListItem>
                    <ListItemAvatar>
                      <Avatar>
                        {member.userName?.[0] || 'U'}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={member.userName}
                      secondary={member.role}
                    />
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))}
              {projectMembers.length === 0 && (
                <ListItem>
                  <ListItemText
                    primary="No team members"
                    secondary="Add team members to collaborate"
                  />
                </ListItem>
              )}
            </List>
          </Box>
        </TabPanel>

        {/* Teams Tab */}
        <TabPanel value={tabValue} index={3}>
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">Assigned Teams</Typography>
              <Button 
                variant="contained" 
                startIcon={<GroupIcon />}
                onClick={() => setAssignTeamDialogOpen(true)}
              >
                Assign Team
              </Button>
            </Box>
            <List>
              {projectTeams.map((team) => (
                <React.Fragment key={team.id}>
                  <ListItem>
                    <ListItemAvatar>
                      <Avatar>
                        <GroupIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={team.teamName}
                      secondary={`${team.memberCount} members • Assigned ${format(new Date(team.assignedAt), 'MMM dd, yyyy')}`}
                    />
                    <IconButton 
                      edge="end" 
                      color="error" 
                      onClick={() => handleUnassignTeam(team.teamId)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))}
              {projectTeams.length === 0 && (
                <ListItem>
                  <ListItemText
                    primary="No teams assigned"
                    secondary="Assign teams to bring multiple members to this project"
                  />
                </ListItem>
              )}
            </List>
          </Box>
        </TabPanel>

        {/* Board Tab */}
        <TabPanel value={tabValue} index={4}>
          <DndProvider backend={HTML5Backend}>
            <Box sx={{ p: 2 }}>
              {/* Board Filters */}
              <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField
                  size="small"
                  placeholder="Search tasks..."
                  value={boardSearchTerm}
                  onChange={(e) => setBoardSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                  }}
                  sx={{ minWidth: 200 }}
                />
                <Typography variant="body2" color="text.secondary">
                  {boardFilteredTasks.length} tasks across {sortedStatuses.length} columns
                </Typography>
              </Box>
              {/* Kanban Columns */}
              <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 2 }}>
                {sortedStatuses.map((status) => (
                  <KanbanColumn
                    key={status.id}
                    status={status}
                    tasks={boardFilteredTasks.filter((t: any) => t.statusId === status.id)}
                    onDrop={handleBoardDrop}
                    onTaskClick={(taskId) => navigate(`/tasks/${taskId}`)}
                  />
                ))}
                {sortedStatuses.length === 0 && (
                  <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center', width: '100%' }}>
                    No statuses configured
                  </Typography>
                )}
              </Box>
            </Box>
          </DndProvider>
        </TabPanel>

        {/* Calendar Tab */}
        <TabPanel value={tabValue} index={5}>
          <Box sx={{ p: 2 }}>
            {/* Month Navigation */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, justifyContent: 'center' }}>
              <IconButton onClick={() => setCalendarDate(subMonths(calendarDate, 1))}><PrevIcon /></IconButton>
              <Typography variant="h6" fontWeight="bold" sx={{ minWidth: 180, textAlign: 'center' }}>
                {format(calendarDate, 'MMMM yyyy')}
              </Typography>
              <IconButton onClick={() => setCalendarDate(addMonths(calendarDate, 1))}><NextIcon /></IconButton>
            </Box>
            {/* Calendar Grid */}
            <Paper sx={{ overflow: 'hidden' }}>
              <Grid container>
                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(d => (
                  <Grid item xs={12 / 7} key={d} sx={{
                    p: 1, textAlign: 'center', bgcolor: 'grey.100',
                    borderBottom: '2px solid #e0e0e0', borderRight: '1px solid #e0e0e0',
                  }}>
                    <Typography variant="caption" fontWeight={700}>{d}</Typography>
                  </Grid>
                ))}
              </Grid>
              <Grid container>
                {calendarCells.map(day => {
                  const dayTasks = tasks.filter(t => {
                    if (!t.dueDate) return false;
                    return isSameDay(parseISO(t.dueDate), day);
                  });
                  const isCurrentMonth = isSameMonth(day, calendarDate);
                  const isToday = isSameDay(day, new Date());
                  return (
                    <Grid item xs={12 / 7} key={day.toISOString()} sx={{
                      minHeight: 90, borderBottom: '1px solid #e0e0e0', borderRight: '1px solid #e0e0e0',
                      p: 0.5, opacity: isCurrentMonth ? 1 : 0.3,
                      bgcolor: isToday ? 'primary.50' : 'inherit',
                    }}>
                      <Typography variant="caption" fontWeight={isToday ? 700 : 400}
                        color={isToday ? 'primary' : 'inherit'} sx={{ display: 'block', mb: 0.25 }}>
                        {format(day, 'd')}
                      </Typography>
                      {dayTasks.slice(0, 3).map((t: any) => (
                        <Tooltip key={t.id} title={t.title}>
                          <Box sx={{
                            bgcolor: priorityColors[String(t.priority)] || '#6B7280',
                            color: '#fff', borderRadius: 0.5, px: 0.5, mb: 0.25,
                            fontSize: '0.6rem', overflow: 'hidden', whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis', cursor: 'pointer',
                          }} onClick={() => navigate(`/tasks/${t.id}`)}>
                            {t.title}
                          </Box>
                        </Tooltip>
                      ))}
                      {dayTasks.length > 3 && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                          +{dayTasks.length - 3} more
                        </Typography>
                      )}
                    </Grid>
                  );
                })}
              </Grid>
            </Paper>
            {/* Legend */}
            <Box sx={{ display: 'flex', gap: 2, mt: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
              {[['Low', '#10B981'], ['Medium', '#3B82F6'], ['High', '#F59E0B'], ['Urgent', '#EF4444']].map(([label, color]) => (
                <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 10, height: 10, bgcolor: color, borderRadius: 0.5 }} />
                  <Typography variant="caption">{label}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </TabPanel>

        {/* Gantt Tab */}
        <TabPanel value={tabValue} index={6}>
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Project Timeline</Typography>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {[ViewMode.Day, ViewMode.Week, ViewMode.Month].map(mode => (
                  <Chip
                    key={mode}
                    label={mode}
                    size="small"
                    onClick={() => setGanttViewMode(mode)}
                    color={ganttViewMode === mode ? 'primary' : 'default'}
                    variant={ganttViewMode === mode ? 'filled' : 'outlined'}
                  />
                ))}
              </Box>
            </Box>
            {ganttTasks.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <BarChartIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                <Typography color="text.secondary">No tasks with dates in this project</Typography>
                <Typography variant="caption" color="text.secondary">
                  Tasks need Start Date and Due Date to appear on the Gantt chart
                </Typography>
              </Box>
            ) : (
              <Paper sx={{ overflow: 'hidden' }}>
                <GanttChart
                  tasks={ganttTasks}
                  viewMode={ganttViewMode}
                  onClick={(task) => {
                    if (task.type !== 'project') navigate(`/tasks/${task.id}`);
                  }}
                  onExpanderClick={(task) => setGanttApiData([...ganttApiData])}
                  listCellWidth="200px"
                  columnWidth={ganttViewMode === ViewMode.Month ? 200 : ganttViewMode === ViewMode.Week ? 120 : 50}
                  rowHeight={38}
                  barFill={70}
                  fontSize="12px"
                  headerHeight={50}
                  todayColor="rgba(25, 118, 210, 0.1)"
                  arrowColor="#94A3B8"
                  barCornerRadius={4}
                  ganttHeight={Math.min(500, ganttTasks.length * 38 + 50)}
                />
              </Paper>
            )}
          </Box>
        </TabPanel>
      </Paper>

      {/* Assign Team Dialog */}
      <Dialog open={assignTeamDialogOpen} onClose={() => setAssignTeamDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Assign Team to Project</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Select Team</InputLabel>
            <Select
              value={selectedTeamId}
              onChange={(e) => setSelectedTeamId(e.target.value)}
              label="Select Team"
            >
              {availableTeams
                .filter(team => !projectTeams.some(pt => pt.teamId === team.id))
                .map((team) => (
                  <MenuItem key={team.id} value={team.id}>
                    {team.name} ({team.memberCount} members)
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignTeamDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleAssignTeam} 
            variant="contained"
            disabled={!selectedTeamId}
          >
            Assign
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Project</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Project Name"
              value={editFormData.name}
              onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Description"
              multiline
              rows={4}
              value={editFormData.description}
              onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
              sx={{ mb: 2 }}
            />
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                <TextField
                  label="Start Date"
                  type="date"
                  fullWidth
                  value={editFormData.startDate}
                  onChange={(e) => setEditFormData({ ...editFormData, startDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="End Date"
                  type="date"
                  fullWidth
                  value={editFormData.endDate}
                  onChange={(e) => setEditFormData({ ...editFormData, endDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={editFormData.status}
                label="Status"
                onChange={(e) => setEditFormData({ ...editFormData, status: e.target.value })}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="on-hold">On Hold</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="archived">Archived</MenuItem>
              </Select>
            </FormControl>
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Project Color
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316'].map((color) => (
                  <Box
                    key={color}
                    onClick={() => setEditFormData({ ...editFormData, color })}
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 1,
                      backgroundColor: color,
                      cursor: 'pointer',
                      border: editFormData.color === color ? '3px solid' : '2px solid',
                      borderColor: editFormData.color === color ? 'primary.main' : 'transparent',
                      transition: 'all 0.2s',
                      '&:hover': {
                        transform: 'scale(1.1)',
                      },
                    }}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleEditSave} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Project?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{project.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Task Dialog */}
      <Dialog open={createTaskDialogOpen} onClose={() => setCreateTaskDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create New Task</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Task Title"
              value={newTaskTitle}
              onChange={(e) => setNewTaskTitle(e.target.value)}
              fullWidth
              required
              autoFocus
            />
            
            <TextField
              label="Description"
              value={newTaskDescription}
              onChange={(e) => setNewTaskDescription(e.target.value)}
              multiline
              rows={4}
              fullWidth
            />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Task Type</InputLabel>
                  <Select
                    value={newTaskTypeId}
                    onChange={(e) => setNewTaskTypeId(e.target.value)}
                    label="Task Type"
                  >
                    {taskTypes.map((type) => (
                      <MenuItem key={type.id} value={type.id}>
                        <Box display="flex" alignItems="center" gap={1}>
                          {type.icon && <span>{type.icon}</span>}
                          <span style={{ color: type.color || undefined }}>{type.name}</span>
                          {type.isDefault && <Chip label="Default" size="small" />}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Priority</InputLabel>
                  <Select
                    value={newTaskPriority}
                    onChange={(e) => setNewTaskPriority(e.target.value as any)}
                    label="Priority"
                  >
                    <MenuItem value="low">
                      <Chip label="Low" color="success" size="small" />
                    </MenuItem>
                    <MenuItem value="medium">
                      <Chip label="Medium" color="info" size="small" />
                    </MenuItem>
                    <MenuItem value="high">
                      <Chip label="High" color="warning" size="small" />
                    </MenuItem>
                    <MenuItem value="urgent">
                      <Chip label="Urgent" color="error" size="small" />
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Assignee (Optional)</InputLabel>
                  <Select
                    value={newTaskAssigneeId}
                    onChange={(e) => setNewTaskAssigneeId(e.target.value)}
                    label="Assignee (Optional)"
                  >
                    <MenuItem value="">
                      <em>Unassigned</em>
                    </MenuItem>
                    {availableUsers.map((user) => (
                      <MenuItem key={user.id} value={user.id}>
                        {user.firstName} {user.lastName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Start Date"
                  type="date"
                  fullWidth
                  value={newTaskStartDate}
                  onChange={(e) => setNewTaskStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                {project?.startDate && newTaskStartDate && new Date(newTaskStartDate) < new Date(project.startDate) && (
                  <Typography variant="caption" color="warning.main">
                    Before project start ({format(new Date(project.startDate), 'MMM d, yyyy')})
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Due Date"
                  type="date"
                  fullWidth
                  value={newTaskDueDate}
                  onChange={(e) => setNewTaskDueDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                {project?.endDate && newTaskDueDate && new Date(newTaskDueDate) > new Date(project.endDate) && (
                  <Typography variant="caption" color="error.main">
                    Exceeds project end date ({format(new Date(project.endDate), 'MMM d, yyyy')})
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateTaskDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleCreateTask} 
            variant="contained" 
            disabled={!newTaskTitle.trim() || !newTaskTypeId}
          >
            Create Task
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Member Dialog */}
      <Dialog open={addMemberDialogOpen} onClose={() => setAddMemberDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add Team Member</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
            <InputLabel>Select User</InputLabel>
            <Select
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              label="Select User"
            >
              {availableUsers
                .filter(user => !projectMembers.some(pm => pm.userId === user.id))
                .map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.fullName} ({user.email})
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Role</InputLabel>
            <Select
              value={selectedMemberRole}
              onChange={(e) => setSelectedMemberRole(e.target.value)}
              label="Role"
            >
              {availableRoles.map((role) => (
                <MenuItem key={role.id} value={role.id}>
                  {role.name}
                  {role.description && ` - ${role.description}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddMemberDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleAddMember} 
            variant="contained"
            disabled={!selectedUserId}
          >
            Add Member
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ProjectDetailsPage;
